import { type Currency, FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { maxNumber, minNumber } from '@ps/web-shared-validators';
import React from 'react';
import { useField } from 'react-final-form';

import type { Limits } from '../../../../types/responsibleGamingLimits';
import {
  validateAmountLimit,
  validateResponsibleGamingLimit
} from '../../../../validators/responsibleGamingLimits';
import AmountCurrency from '../../../AmountCurrency/AmountCurrency';
import ConversionSubLabel from '../../../ManageCurrencies/components/ConversionSubLabel/ConversionSubLabel';
import useFormatDisabledLimit from '../../hooks/useFormatDisabledLimit';

import {
  TXTWCL_Must_be_higher_than_or_equal_t_ELL,
  TXTWCL_Must_be_higher_than_or_equal_t_ELL_1,
  TXTWCL_N0_Day_limit,
  TXTWCL_The_maximum_amount_for_this_li_ELL_1,
  TXTWCL_The_minimum_amount_for_this_li_ELL
} from './MonthlyLimit.messages.json';

interface Props {
  fieldName: string;
  limits: Limits;
  currency: Currency;
  limitsDisabled?: boolean;
  showConversionInfo?: boolean;
  withPlaceholder?: boolean;
}

const MonthlyLimit: React.FC<Props> = ({
  fieldName,
  currency,
  limits,
  limitsDisabled,
  withPlaceholder = false,
  ...props
}) => {
  const showConversionInfo = useFeature(FeatureFlag.SwcDepositLimitCurrencyConversionInfo);
  const { limitMax, limitMin, limit } = limits?.monthly ?? {};

  useField(`${fieldName}.monthly.previousLimit`, { initialValue: limit });
  const {
    input: { value: rate }
  } = useField(`${fieldName}.rate`, { subscription: { value: true } });
  useField(`${fieldName}.weekly.previousLimit`, { initialValue: limit });
  const {
    input: { onChange: onDepositLimitsChange, value: monthlyLimit }
  } = useField(`${fieldName}.monthly.limit`, {
    initialValue: limit !== -1 ? limit : undefined,
    subscription: { value: true }
  });

  useFormatDisabledLimit(limitsDisabled, limit, onDepositLimitsChange);

  return (
    <AmountCurrency
      withPlaceholder={withPlaceholder}
      disabled={limitsDisabled}
      defaultCurrency={currency}
      includeCurrencies={[currency]}
      label={TXTWCL_N0_Day_limit}
      values={{ 0: 30 }}
      currencyFieldName={`${fieldName}.currency`}
      amountFieldName={`${fieldName}.monthly.limit`}
      onAmountBlur={() => {
        monthlyLimit && onDepositLimitsChange(Number(monthlyLimit).toFixed(2));
      }}
      extension={
        showConversionInfo && <ConversionSubLabel amount={monthlyLimit} fromCurrency={currency} />
      }
      validators={[
        validateAmountLimit(
          minNumber,
          TXTWCL_The_minimum_amount_for_this_li_ELL,
          limitMin,
          fieldName,
          rate
        ),
        validateAmountLimit(
          maxNumber,
          TXTWCL_The_maximum_amount_for_this_li_ELL_1,
          limitMax,
          fieldName,
          rate
        ),
        validateResponsibleGamingLimit(
          `${fieldName}.daily.limit`,
          limit,
          { ...TXTWCL_Must_be_higher_than_or_equal_t_ELL },
          minNumber
        ),
        validateResponsibleGamingLimit(
          `${fieldName}.weekly.limit`,
          limit,
          { ...TXTWCL_Must_be_higher_than_or_equal_t_ELL_1, values: { 0: 7 } },
          minNumber
        )
      ]}
      {...props}
    />
  );
};

export default MonthlyLimit;
